import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://www.haus.lv";
const SITE_NAME = "HAUS";
const DEFAULT_OG_IMAGE = "https://storage.googleapis.com/gpt-engineer-file-uploads/EbNs9gFrynMvscONU9271icq0hz1/social-images/social-1766164843125-haus (3).png";
const LANGUAGES = ["en", "lv", "ru"] as const;

interface SeoHeadProps {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: string;
  noIndex?: boolean;
  jsonLd?: Record<string, unknown>;
}

/**
 * Sets document title, meta description, canonical, hreflang, OG/Twitter tags,
 * and optional JSON-LD structured data.
 */
export const SeoHead = ({
  title,
  description,
  ogImage,
  ogType = "website",
  noIndex = false,
  jsonLd,
}: SeoHeadProps) => {
  const location = useLocation();

  useEffect(() => {
    // --- Title ---
    document.title = title;

    // --- Helper to set/create meta tags ---
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    // --- Description ---
    setMeta("name", "description", description);

    // --- Robots ---
    if (noIndex) {
      setMeta("name", "robots", "noindex, nofollow");
    } else {
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    }

    // --- Canonical ---
    const canonicalUrl = `${SITE_URL}${location.pathname}`;
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    // --- Hreflang tags ---
    // Remove old hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    // Determine the path without the language prefix
    const pathMatch = location.pathname.match(/^\/(en|lv|ru)(\/.*)?$/);
    const pathWithoutLang = pathMatch ? (pathMatch[2] || "") : location.pathname;
    const searchString = location.search || "";

    for (const lang of LANGUAGES) {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", lang);
      link.setAttribute("href", `${SITE_URL}/${lang}${pathWithoutLang}${searchString}`);
      document.head.appendChild(link);
    }

    // x-default
    const xDefault = document.createElement("link");
    xDefault.setAttribute("rel", "alternate");
    xDefault.setAttribute("hreflang", "x-default");
    xDefault.setAttribute("href", `${SITE_URL}/en${pathWithoutLang}${searchString}`);
    document.head.appendChild(xDefault);

    // --- Open Graph ---
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:type", ogType);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:image", ogImage || DEFAULT_OG_IMAGE);

    // --- Twitter ---
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setMeta("name", "twitter:image", ogImage || DEFAULT_OG_IMAGE);

    // --- JSON-LD ---
    const existingJsonLd = document.querySelector('script[data-seo-jsonld]');
    if (existingJsonLd) existingJsonLd.remove();

    if (jsonLd) {
      const script = document.createElement("script");
      script.setAttribute("type", "application/ld+json");
      script.setAttribute("data-seo-jsonld", "true");
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Cleanup on unmount
    return () => {
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
      const jsonLdScript = document.querySelector('script[data-seo-jsonld]');
      if (jsonLdScript) jsonLdScript.remove();
    };
  }, [title, description, ogImage, ogType, noIndex, jsonLd, location.pathname, location.search]);

  return null;
};
